import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Droplets, Car, Truck, Wrench, Users } from "lucide-react";

const SERVICE_TYPES = [
  { key: "carwash", label: "Carwash", icon: Droplets },
  { key: "used_car_detail", label: "Used Car Detail", icon: Car },
  { key: "porter", label: "Porter", icon: Truck },
  { key: "bodyshop", label: "Bodyshop", icon: Wrench },
  { key: "onboarding", label: "Onboarding", icon: Users },
];

const STATUSES = ["pending", "in_progress", "completed", "cancelled"];

const STATUS_STYLES = {
  pending: "bg-zinc-100 text-black border-zinc-300",
  in_progress: "bg-[#FF2B00] text-white border-[#FF2B00]",
  completed: "bg-black text-white border-black",
  cancelled: "bg-white text-zinc-400 border-zinc-300 line-through",
};

export default function Services() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState("");
  const [dealerships, setDealerships] = useState([]);
  const [customTypes, setCustomTypes] = useState([]);
  const [form, setForm] = useState({
    service_type: "carwash", dealership_id: "", dealership_name: "",
    vehicle_make: "", vehicle_model: "", vehicle_year: "", vehicle_vin: "",
    notes: "", priority: "normal",
  });

  const isStaff = user && user.role !== "dealership";

  const load = async () => {
    const params = {};
    if (filterType) params.service_type = filterType;
    if (filterStatus) params.status = filterStatus;
    const { data } = await api.get("/services", { params });
    setItems(data);
  };

  useEffect(() => { load(); }, [filterType, filterStatus]);
  useEffect(() => { api.get("/dealerships").then(({ data }) => setDealerships(data)).catch(() => {}); }, []);

  // Re-fetch custom service types every time the create modal opens, so changes
  // made in /app/customize show up immediately without a page reload.
  useEffect(() => {
    if (showCreate) {
      api.get("/settings").then(({ data }) => setCustomTypes(data?.custom_service_types || [])).catch(() => {});
    }
  }, [showCreate]);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form, vehicle_year: form.vehicle_year ? parseInt(form.vehicle_year) : null };
      if (!payload.vehicle_year) delete payload.vehicle_year;
      if (!payload.dealership_id) delete payload.dealership_id;
      await api.post("/services", payload);
      setShowCreate(false);
      setForm({ ...form, vehicle_make: "", vehicle_model: "", vehicle_year: "", vehicle_vin: "", notes: "" });
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/services/${id}`, { status });
    load();
  };

  return (
    <div className="p-6 lg:p-12" data-testid="services-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-caps text-[#FF2B00]">Service Operations</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Service Requests</h1>
        </div>
        <button
          data-testid="services-new-btn"
          onClick={() => setShowCreate(true)}
          className="bg-black text-white px-5 py-3 hover:bg-[#FF2B00] label-caps inline-flex items-center gap-2"
        >
          <Plus size={16} /> New Request
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setFilterType("")} className={`label-caps px-3 py-2 border ${filterType === "" ? "bg-black text-white border-black" : "border-zinc-300"}`} data-testid="filter-all">All</button>
        {SERVICE_TYPES.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            data-testid={`filter-${key}`}
            onClick={() => setFilterType(key)}
            className={`label-caps px-3 py-2 border inline-flex items-center gap-2 ${filterType === key ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"}`}
          >
            <Icon size={12} /> {label}
          </button>
        ))}
        <select
          data-testid="filter-status"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="border border-zinc-300 px-3 py-2 label-caps bg-white"
        >
          <option value="">All Statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
      </div>

      {/* List */}
      <div className="bg-white border border-zinc-200">
        <div className="hidden md:grid grid-cols-12 gap-4 p-4 border-b border-zinc-200 label-caps text-zinc-500">
          <div className="col-span-2">Type</div>
          <div className="col-span-3">Dealership</div>
          <div className="col-span-3">Vehicle</div>
          <div className="col-span-2">Priority</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        {items.length === 0 && <div className="p-8 text-zinc-500 text-center" data-testid="services-empty">No service requests yet.</div>}
        {items.map((it) => (
          <div key={it.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 p-4 border-b border-zinc-100 hover:bg-zinc-50 items-center" data-testid={`service-row-${it.id}`}>
            <div className="col-span-2 font-medium capitalize">{it.service_type.replace("_", " ")}</div>
            <div className="col-span-3 text-sm">{it.dealership_name}</div>
            <div className="col-span-3 text-sm text-zinc-600">
              {[it.vehicle_year, it.vehicle_make, it.vehicle_model].filter(Boolean).join(" ") || "—"}
              {it.vehicle_vin && <div className="text-xs text-zinc-400 font-mono">{it.vehicle_vin}</div>}
            </div>
            <div className="col-span-2"><span className="label-caps text-xs">{it.priority}</span></div>
            <div className="col-span-2 text-right">
              {isStaff ? (
                <select
                  data-testid={`status-${it.id}`}
                  value={it.status}
                  onChange={(e) => updateStatus(it.id, e.target.value)}
                  className={`label-caps text-xs border px-2 py-1 ${STATUS_STYLES[it.status]}`}
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
              ) : (
                <span className={`label-caps text-xs px-2 py-1 border ${STATUS_STYLES[it.status]}`}>{it.status.replace("_", " ")}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="service-create-modal">
            <div className="flex items-center justify-between p-5 border-b border-zinc-200">
              <h2 className="font-display text-2xl">New Service Request</h2>
              <button onClick={() => setShowCreate(false)} data-testid="service-create-close"><X size={20} /></button>
            </div>
            <form onSubmit={create} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label-caps block mb-2">Service Type</label>
                <select data-testid="service-type" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} className="w-full border border-black px-3 py-2 bg-white">
                  {SERVICE_TYPES.map(({ key, label }) => <option key={key} value={key}>{label}</option>)}
                  {customTypes.length > 0 && <optgroup label="Custom">
                    {customTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </optgroup>}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label-caps block mb-2">Dealership</label>
                <select
                  data-testid="service-dealership"
                  value={form.dealership_id}
                  onChange={(e) => {
                    const d = dealerships.find((x) => x.id === e.target.value);
                    setForm({ ...form, dealership_id: e.target.value, dealership_name: d?.name || "" });
                  }}
                  className="w-full border border-black px-3 py-2 bg-white"
                >
                  <option value="">— Pick a dealership —</option>
                  {dealerships.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <input
                  data-testid="service-dealership-name"
                  placeholder="Or type dealership name"
                  value={form.dealership_name}
                  onChange={(e) => setForm({ ...form, dealership_name: e.target.value })}
                  className="mt-2 w-full border border-zinc-300 px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="label-caps block mb-2">Make</label>
                <input data-testid="service-make" value={form.vehicle_make} onChange={(e) => setForm({ ...form, vehicle_make: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" />
              </div>
              <div>
                <label className="label-caps block mb-2">Model</label>
                <input data-testid="service-model" value={form.vehicle_model} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" />
              </div>
              <div>
                <label className="label-caps block mb-2">Year</label>
                <input data-testid="service-year" type="number" value={form.vehicle_year} onChange={(e) => setForm({ ...form, vehicle_year: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" />
              </div>
              <div>
                <label className="label-caps block mb-2">VIN</label>
                <input data-testid="service-vin" value={form.vehicle_vin} onChange={(e) => setForm({ ...form, vehicle_vin: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 font-mono" />
              </div>
              <div className="md:col-span-2">
                <label className="label-caps block mb-2">Priority</label>
                <select data-testid="service-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white">
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label-caps block mb-2">Notes</label>
                <textarea data-testid="service-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full border border-zinc-300 px-3 py-2" />
              </div>
              {error && <div className="md:col-span-2 text-sm text-[#FF2B00]">{error}</div>}
              <div className="md:col-span-2 flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="border border-black px-5 py-2 label-caps">Cancel</button>
                <button data-testid="service-create-submit" type="submit" className="bg-black text-white px-5 py-2 hover:bg-[#FF2B00] label-caps">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
